import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "button",
  "[href]",
  "input",
  "select",
  "textarea",
  "[tabindex]",
  '[contenteditable="true"]',
  '[contenteditable=""]',
].join(", ");

function isAvailableForFocus(element) {
  if (
    element.matches(":disabled") ||
    element.getAttribute("aria-disabled") === "true" ||
    element.getAttribute("type") === "hidden" ||
    (element.hasAttribute("tabindex") && Number(element.getAttribute("tabindex")) < 0) ||
    element.closest('[hidden], [inert], [aria-hidden="true"]')
  ) {
    return false;
  }

  const view = element.ownerDocument.defaultView;
  for (let node = element; node; node = node.parentElement) {
    const style = view?.getComputedStyle(node);
    if (style?.display === "none" || style?.visibility === "hidden" || style?.visibility === "collapse") {
      return false;
    }
  }
  return true;
}

/** Keep keyboard focus inside the currently open Booking Engine settings dialog. */
export default function useBookingSettingsDialog({ rootRef, dialogKey, onClose, busy }) {
  const latest = useRef({ onClose, busy });
  latest.current = { onClose, busy };

  useEffect(() => {
    if (!dialogKey) return undefined;
    const dialogs = rootRef.current?.querySelectorAll(".beStudio__modal");
    const dialog = dialogs?.[dialogs.length - 1];
    if (!dialog) return undefined;

    const ownerDocument = dialog.ownerDocument;
    const previousFocus = ownerDocument.activeElement;
    const focus = (element) => element.focus({ preventScroll: true });
    const focusables = () => Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(isAvailableForFocus)
      .sort((left, right) => {
        // Positive tabindex values come before the natural document-order group.
        const leftOrder = left.tabIndex > 0 ? left.tabIndex : Infinity;
        const rightOrder = right.tabIndex > 0 ? right.tabIndex : Infinity;
        return leftOrder === rightOrder ? 0 : leftOrder - rightOrder;
      });

    focus(dialog);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (!latest.current.busy) latest.current.onClose?.();
        return;
      }
      if (event.key !== "Tab") return;

      const elements = focusables();
      const first = elements[0] || dialog;
      const last = elements[elements.length - 1] || dialog;
      const active = ownerDocument.activeElement;
      if (!elements.includes(active)) {
        event.preventDefault();
        focus(event.shiftKey ? last : first);
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        focus(last);
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        focus(first);
      }
    };

    const handleFocusIn = (event) => {
      if (!dialog.contains(event.target)) focus(dialog);
    };

    ownerDocument.addEventListener("keydown", handleKeyDown);
    ownerDocument.addEventListener("focusin", handleFocusIn);
    return () => {
      ownerDocument.removeEventListener("keydown", handleKeyDown);
      ownerDocument.removeEventListener("focusin", handleFocusIn);
      if (previousFocus?.isConnected && typeof previousFocus.focus === "function") {
        focus(previousFocus);
      }
    };
  }, [dialogKey, rootRef]);
}
